import ConfirmModal from 'v2/components/ConfirmModal';
import DataTable from 'v2/components/DataTable';
import EmptyState from 'v2/components/EmptyState';
import LoadingBar from 'v2/components/LoadingBar';
import OutlinedButton from 'v2/components/OutlinedButton';
import RowActionsMenu from 'v2/components/RowActionsMenu';
import useInvitationsTable from 'v2/layouts/OrganizationWorkspaceLayout/components/InvitationsTableSection/hooks/useInvitationsTable';

import useDateFormat from 'hooks/useDateFormat';

import useTranslation from './hooks/useTranslation';

import {
    ACTIONS_COLUMN_KEY,
    ACTIONS_COLUMN_WIDTH,
    EMAIL_COLUMN_KEY,
    EMPTY_CELL_TEXT,
    EXPIRES_COLUMN_KEY,
    ROLE_COLUMN_KEY,
    ROLE_SEPARATOR,
    SENT_COLUMN_KEY,
    STATUS_COLUMN_KEY,
    TEST_ID
} from './constants';
import type { DataTableColumn, DataTableRow, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const InvitationsTableSection: FunctionComponent<Props> = ({
    className = '',
    testId = TEST_ID
}) => {
    const {
        handleDeleteCancel,
        handleDeleteConfirm,
        handleDeleteRequest,
        handleLoadMore,
        handleResend,
        hasNextPage,
        invitations,
        isDeleteConfirmOpen,
        isFetchingMore,
        isLoading
    } = useInvitationsTable();

    const { formatDate } = useDateFormat();

    const {
        ACTIONS_LABEL,
        DELETE,
        DELETE_CONFIRM_CANCEL,
        DELETE_CONFIRM_LABEL,
        DELETE_CONFIRM_MESSAGE,
        DELETE_CONFIRM_TITLE,
        EMAIL,
        EMPTY_DESCRIPTION,
        EMPTY_HEADING,
        EXPIRES,
        LOAD_MORE,
        RESEND,
        ROLE,
        SENT,
        STATUS,
        TABLE_LABEL
    } = useTranslation();

    const columns: DataTableColumn[] = [
        { key: EMAIL_COLUMN_KEY, label: EMAIL },
        { key: ROLE_COLUMN_KEY, label: ROLE },
        { key: SENT_COLUMN_KEY, label: SENT },
        { key: EXPIRES_COLUMN_KEY, label: EXPIRES },
        { key: STATUS_COLUMN_KEY, label: STATUS },
        { key: ACTIONS_COLUMN_KEY, label: '', width: ACTIONS_COLUMN_WIDTH }
    ];

    const rows: DataTableRow[] = invitations.map(invitation => ({
        [ACTIONS_COLUMN_KEY]: (
            <RowActionsMenu
                actions={[
                    { label: RESEND, onClick: () => handleResend(invitation) },
                    {
                        isDestructive: true,
                        label: DELETE,
                        onClick: () => handleDeleteRequest(invitation.id)
                    }
                ]}
                ariaLabel={ACTIONS_LABEL}
            />
        ),
        [EMAIL_COLUMN_KEY]: invitation.invitee || EMPTY_CELL_TEXT,
        [EXPIRES_COLUMN_KEY]: formatDate(invitation.expiresAt),
        [ROLE_COLUMN_KEY]: invitation.roles.join(ROLE_SEPARATOR) || EMPTY_CELL_TEXT,
        [SENT_COLUMN_KEY]: formatDate(invitation.createdAt),
        [STATUS_COLUMN_KEY]: invitation.status || EMPTY_CELL_TEXT
    }));

    if (isLoading) {
        return <LoadingBar className={className} testId={testId} />;
    }

    if (invitations.length === 0) {
        return (
            <EmptyState
                className={className}
                description={EMPTY_DESCRIPTION}
                heading={EMPTY_HEADING}
                testId={testId}
            />
        );
    }

    return (
        <div className={`${className} ${styles.invitationsTableSection}`} data-testid={testId}>
            <div className={styles.tableScroll}>
                <DataTable ariaLabel={TABLE_LABEL} columns={columns} rows={rows} />
            </div>

            {hasNextPage ? (
                <OutlinedButton
                    className={styles.loadMore}
                    disabled={isFetchingMore}
                    onClick={handleLoadMore}
                >
                    {LOAD_MORE}
                </OutlinedButton>
            ) : null}

            <ConfirmModal
                cancelLabel={DELETE_CONFIRM_CANCEL}
                confirmLabel={DELETE_CONFIRM_LABEL}
                isDestructive
                isOpen={isDeleteConfirmOpen}
                message={DELETE_CONFIRM_MESSAGE}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={DELETE_CONFIRM_TITLE}
            />
        </div>
    );
};

export default InvitationsTableSection;
